import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';

import { CommonService } from '../../services/common.service';
import { ErrorService } from '../../services/error/error.service';
import { FileService } from '../../services/file.service';
import { LocalStorageService } from '../../services/local-storage.service';
import {
  DocumentDataObject,
  InvoiceDataObject,
  OfferDataObject,
  OrderDataObject,
  OrderProcessApplication,
  OrderProcessData,
  OrderProcessResponse,
} from '../../jurisdiction-overview/jurisdiction-overview.domain';
import { OrderProcessService } from './order-process.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageType } from '../../constants/app.constants';
import { SubmitService } from '../../services/submit.service';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import { ChimneySweeper, GetChimneySweeperResponse, LookupOptionsData } from '../jurisdiction.domain';
import { NetzTranslateService } from '../../services/netz-translate.service';
import { CommunicationService } from '../../services/communication.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { FileUpload } from 'primeng';
import { ReviewSectionStore } from 'src/app/+shared/components/review-section/review-section.store';
import { SectionStatus } from 'src/app/+shared/components/review-section/review-section.domain';
import { map } from 'rxjs/operators';
import { OrderProcesStore } from 'src/app/+shared/stores/order-process.store';

@Component({
  providers: [ConfirmationService, OrderProcessService],
  selector: 'app-order-process',
  styleUrls: ['./order-process.component.scss'],
  templateUrl: './order-process.component.html',
})
export class OrderProcessComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean = false;
  @Input() isInstallationFlow: boolean = false;
  @Input() isConnectionFlow: boolean = false;

  @Output() executorActionEvent = new EventEmitter<any>();

  chimneySweeper: ChimneySweeper;
  chimneySweeperOptions: ChimneySweeper[];

  isLoading: boolean;

  offerData: OfferDataObject;
  invoiceData: InvoiceDataObject;
  orderData: OrderDataObject;

  orderProcessesKey: string = AllDropdownKeys.OrderProcess;
  orderProcesses: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.OrderProcess);

  orderProcessData: OrderProcessData;
  orderProcessDetails: OrderProcessData;
  orderProcessFG: FormGroup;

  role: string = this.localStorageService.getItem('role');
  technicianId: string = this.localStorageService.getItem('technician_id');

  showRequestChangeBtn: boolean = this.environmentService.showRequestChangeBtn;
  showAcceptRejectApplicationBtns: boolean;
  applicationType: string = JSON.parse(localStorage.getItem('application_type'));
  showApplicationIdInOffer: boolean = this.environmentService.showApplicationIdInOffer;
  showOrderOfferText: boolean = this.environmentService.showOrderOfferText && this.isExternalUser;

  sectionType = {
    Offer: 'offer_submitted',
    Order: 'order_submitted',
    Invoice: 'invoice_submitted',
  };

  docIdTypes = {
    offer: 'offer_id',
    order: 'order_id',
    invoice: 'invoice_id',
  };
  areAllSectionsAccepted$: any;
  areAnySectionRejected$: any;
  isApplicationCompleted: boolean;
  areAnySectionUnreviewed$: any;

  constructor(
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private fileService: FileService,
    private localStorageService: LocalStorageService,
    private orderProcessService: OrderProcessService,
    private router: Router,
    private submitService: SubmitService,
    private netzTranslateService: NetzTranslateService,
    private environmentService: EnvironmentService,
    private reviewSectionStore: ReviewSectionStore,
    private orderProcessStore: OrderProcesStore,
    private route: ActivatedRoute
  ) {
    super();
  }

  get disableSubmitOfferButton(): boolean {
    return this.isReadOnly || this.orderProcessData.sk_executor_action !== 'approve_offer' || !this.offerData?.document?.length;
  }

  get disableSubmitOrderButton(): boolean {
    return this.isReadOnly || this.orderProcessData.sk_executor_action !== 'approve_order' || !this.orderData?.document?.length;
  }

  get disableSubmitInvoiceButton(): boolean {
    return this.isReadOnly || this.orderProcessData.sk_executor_action !== 'approve_invoice' || !this.invoiceData?.document?.length;
  }

  get isInternalUser(): boolean {
    return this.role === 'employee' || this.role === 'administrator';
  }

  get isExternalUser(): boolean {
    return this.role === 'customer' || this.role === 'standard_technician' || this.role === 'guest_technician';
  }

  get isChimneySweeper(): boolean {
    return this.role === 'chimney_sweeper';
  }

  get standardOfferApplicationIdTitle(): string {
    return this.showApplicationIdInOffer
      ? this.netzTranslateService.translateString('overview.process-details.standard-offer-application-id-title', {
          value: this.orderProcessData.application_ext_id,
        })
      : null;
  }

  get showDownloadOfferSection(): boolean {
    return (
      this.isInternalUser ||
      (this.isExternalUser &&
        this.isConnectionFlow &&
        !['offer_created', 'application_wait_for_update', 'offer_rejected'].includes(this.orderProcessData.sk_orderprocess_status))
    );
  }

  ngOnInit(): void {
    this.preventLeak(this.route.queryParams).subscribe(params => {
      const onSuccess = () => {
        this.showAcceptRejectApplicationBtns =
          this.isInternalUser &&
          (this.applicationType === 'installation' || this.environmentService.showAcceptRejectApplicationBtns) &&
          ['application_accepted', 'application_rejected'].indexOf(this.orderProcessData?.sk_orderprocess_status) === -1;
      };

      this.getOrderProcessDetails(params.orderProcessId, onSuccess);
    });

    if (this.isChimneySweeper) {
      this.getChimneySweeperDetails();
    }

    const sectionReviews$ = this.reviewSectionStore.reviews;
    this.areAllSectionsAccepted$ = sectionReviews$.pipe(
      map(sectionReviews =>
        sectionReviews
          .filter(r => r.application_id == this.orderProcessData.application_id)
          .every(sectionReview => sectionReview.status === SectionStatus.approved)
      )
    );
    this.areAnySectionRejected$ = sectionReviews$.pipe(
      map(sectionReviews => sectionReviews.some(sectionReview => sectionReview.status === SectionStatus.rejected))
    );
    this.areAnySectionUnreviewed$ = sectionReviews$.pipe(
      map(sectionReviews => sectionReviews.some(sectionReview => sectionReview.status === SectionStatus.none))
    );
  }

  downloadDocument(document: DocumentDataObject): void {
    this.preventLeak(this.orderProcessService.readDocument(document.document_id)).subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.fileService.downloadDocument(response, document.filename);
      },
      error => this.errorService.throwErrorIfFailure(error, true)
    );
  }

  uploadDocument(
    file: File,
    docIdType: string,
    uploader: FileUpload,
    documentControl: AbstractControl,
    data?: { document?: DocumentDataObject[] }
  ) {
    documentControl.setValue(file.name);

    const documentId = data?.document?.find(() => true)?.document_id;
    const docId = (data || {})[docIdType];

    let uploadRequest = this.orderProcessService.uploadDocument(file, documentId, docIdType, docId);

    if (!documentId && docIdType === 'offer_id' && !this.environmentService.createAutomaticOffer) {
      uploadRequest = this.orderProcessService.uploadOfferDocument(file, docId);
    }

    this.preventLeak(uploadRequest).subscribe(() => {
      uploader.clear();
      this.route.queryParams.subscribe(params => {
        this.getOrderProcessDetails(params.orderProcessId);
      });
    });
  }

  updateOrderProcess(rejectOffer: boolean = false, status?: string): void {
    const offerAccepted = !rejectOffer;
    this.cdr.detectChanges();

    this.confirmationService.confirm({
      message: this.netzTranslateService.translateString(this.getMessageForDialog(status, offerAccepted)),
      accept: () =>
        this.preventLeak(
          this.orderProcessService.updateOrderProcessData(this.orderProcessData.orderprocess_id, {
            sk_executor_action: this.getNextExecutorAction(offerAccepted, status),
          })
        ).subscribe(
          (response: OrderProcessResponse) => {
            this.errorService.throwErrorIfFailure(response, true);

            if (this.role === 'customer') {
              this.postSuccessfulOfferUpdate(offerAccepted);
            } else {
              this.commonService.showHighlightedRow = this.orderProcessData.application_id;
              this.router.navigate(['/jurisdiction-overview']);
            }
          },
          error => this.errorService.throwErrorIfFailure(error, true)
        ),
      reject: () => this.confirmationService.close(),
    });
  }

  changeChimneySweeper() {
    this.confirmationService.confirm({
      message: this.netzTranslateService.translateString('overview.process-details.change-chimney-sweeper-text'),
      accept: () => {
        this.isLoading = true;
        const applicationId = this.orderProcessData?.application_id;
        this.communicationService
          .updateData(`${UrlSegments.InstallationApplication}${applicationId}`, {
            chimney_sweeper_id: this.chimneySweeper?.chimney_sweeper_id,
          })
          .subscribe(
            response => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(response, true);
              this.confirmationService.close();
              this.router.navigate(['/jurisdiction-overview']);
            },
            error => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(error, true);
            }
          );
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  showSection(sectionType: string): boolean {
    const currentStatus = this.orderProcesses.filter(item => item.value === this.orderProcessData.sk_orderprocess_status);
    const submitSectionObject = this.orderProcesses.find(item => item.value === sectionType);

    return (
      (this.isExternalUser &&
        (currentStatus.length && Number(currentStatus[0].order)) >= (submitSectionObject ? Number(submitSectionObject.order) : 0)) ||
      this.isInternalUser
    );
  }

  private prepareForm(): void {
    if (this.isInstallationFlow) {
      return;
    }

    this.orderProcessFG = new FormGroup({
      standardOfferValue: new FormControl({
        value: this.offerData?.sk_offer_type === 'automatic' ? 'false' : 'true',
        disabled: this.isReadOnly,
      }),
      orderValue: new FormControl({
        value: this.orderData?.sk_offer_type === 'automatic' ? 'false' : 'true',
        disabled: this.isReadOnly,
      }),
      invoiceValue: new FormControl({
        value: this.invoiceData?.sk_offer_type === 'automatic' ? 'false' : 'true',
        disabled: this.isReadOnly,
      }),
      standardOfferDocument: new FormControl({
        value: this.offerData?.document?.length ? this.offerData.document[0].filename : '',
        disabled: true,
      }),
      orderDocument: new FormControl({
        value: this.orderData?.document?.length ? this.orderData.document[0].filename : '',
        disabled: true,
      }),
      invoiceDocument: new FormControl({
        value: this.invoiceData?.document?.length ? this.invoiceData.document[0].filename : '',
        disabled: true,
      }),
    });

    if (this.environmentService.showSapOfferAndOrderId) {
      this.orderProcessFG.addControl(
        'sapOfferId',
        new FormControl({
          value: this.offerData?.offer_no ?? '',
          disabled: true,
        })
      );
      this.orderProcessFG.addControl(
        'sapOrderId',
        new FormControl({
          value: this.orderData?.order_no ?? '',
          disabled: true,
        })
      );
    }
  }

  private getOrderProcessDetails(orderProcessId: string, onSuccess?: any): void {
    this.isLoading = true;
    this.preventLeak(this.orderProcessService.getOrderProcessData(orderProcessId)).subscribe(
      (response: OrderProcessResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);

        if (response.orderprocess && response.orderprocess.length) {
          if (this.isInstallationFlow) {
            this.orderProcessDetails = response.orderprocess[0];
          } else {
            this.invoiceData = response.orderprocess[0].invoice;
            this.offerData = response.orderprocess[0].offer;
            this.orderData = response.orderprocess[0].order;
          }

          if (this.isConnectionFlow) {
            this.orderProcessDetails = response.orderprocess[0];
          }

          this.orderProcessStore.setOrderProcessData(response.orderprocess[0]);
          this.orderProcessData = response.orderprocess[0];

          onSuccess && onSuccess();
        }

        this.prepareForm();

        if (this.orderProcessDetails?.sk_executor_action) {
          this.executorActionEvent.emit(this.orderProcessDetails.sk_executor_action);
        }

        if (this.orderProcessDetails?.sk_orderprocess_status === 'application_rejected') {
          this.isApplicationCompleted = [
            'cs_application_accepted',
            'cs_application_rejected',
            'application_accepted',
            'application_rejected',
            'closed',
          ].includes(this.orderProcessDetails?.sk_orderprocess_status);
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private getChimneySweeperDetails() {
    this.isLoading = true;
    this.communicationService.getData('chimneysweeper').subscribe(
      (response: GetChimneySweeperResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.chimneySweeperOptions = response.chimneysweeper;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error);
      }
    );
  }

  private postSuccessfulOfferUpdate(offerAccepted: boolean): void {
    const message = offerAccepted ? 'overview.process-details.accept-offer-text' : 'overview.process-details.reject-offer-text';
    this.submitService.setMessageText(message);
    this.submitService.setMessage(MessageType.DYNAMIC);

    this.router.navigate(['/submitted']);
  }

  private getMessageForDialog(status: string, offerAccepted: boolean): string {
    if (this.role === 'customer') {
      return offerAccepted
        ? 'overview.process-details.confirmation-text-accept-offer'
        : 'overview.process-details.confirmation-text-reject-offer';
    }

    if (offerAccepted) {
      if (this.isInstallationFlow) {
        return 'overview.process-details.confirmation-text-accept-application';
      }

      if (!this.disableSubmitOfferButton) {
        return 'overview.process-details.confirmation-text-offer';
      }

      if (!this.disableSubmitOrderButton) {
        return 'overview.process-details.confirmation-text-order';
      }
      if (!this.disableSubmitInvoiceButton) {
        return 'overview.process-details.confirmation-text-invoice';
      }

      if (this.orderProcessData.sk_executor_action === 'accept_offer' || this.orderProcessData.sk_executor_action === 'approve_offer') {
        return 'overview.process-details.confirmation-text-accept-offer';
      }

      if (this.orderProcessData.sk_executor_action === 'accept_application') {
        return 'overview.process-details.confirmation-text-accept-application';
      }
    }

    if (this.isInstallationFlow) {
      if (status) {
        return status === 'request_update_application'
          ? 'overview.process-details.confirmation-text-request-change'
          : 'overview.process-details.confirmation-text-update-application';
      }

      return 'overview.process-details.confirmation-text-reject-application';
    }

    if (this.isConnectionFlow && status === 'request_update_application') {
      return 'overview.process-details.confirmation-text-request-change';
    }

    if (this.isConnectionFlow && status === 'update_application') {
      return 'overview.process-details.confirmation-text-request-change';
    }

    if (this.isConnectionFlow && !status) {
      return 'overview.process-details.confirmation-text-reject-application';
    }

    if (this.orderProcessData.sk_executor_action === 'accept_offer' || this.orderProcessData.sk_executor_action === 'approve_offer') {
      return 'overview.process-details.confirmation-text-reject-offer';
    }

    if (this.orderProcessData.sk_executor_action === 'accept_application') {
      return 'overview.process-details.confirmation-text-reject-application';
    }
  }

  private getNextExecutorAction(offerAccepted: boolean, status?: string): string {
    if (offerAccepted) {
      if (this.isInstallationFlow) {
        return this.isChimneySweeper ? 'cs_application_accepted' : 'application_accepted';
      }

      if (status === 'accept_offer') {
        return 'accept_offer';
      }

      return this.orderProcessData.sk_executor_action;
    }

    if (status === 'reject_offer') {
      return 'reject_offer';
    }

    if (status === 'request_update_application') {
      return 'request_update_application';
    }

    if (status === 'update_application') {
      return 'update_application';
    }

    if (status === 'cs_request_update_application') {
      return 'cs_request_update_application';
    }

    if (this.isInstallationFlow) {
      if (this.isChimneySweeper) {
        return 'cs_application_rejected';
      } else {
        return status ? status : 'application_rejected';
      }
    }

    return 'reject_offer';
  }
}
